import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import { compactActionSx, surfaceSx } from "./center/ownerCenterUi";

const DAY_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const DOMAIN_LABELS = {
  personal: "Personal",
  home: "Casa",
  freelance: "Changas / freelance",
  career: "Carrera",
  business: "Negocio",
  finance: "Finanzas",
  other: "Otro",
};

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("es-AR", { weekday: "short", day: "numeric", month: "short" }).format(date);
}

function trackingLabel(row) {
  if (!row?.scheduled) return "Sin ocurrencias programadas";
  return `${row.completed}/${row.scheduled} · ${Math.round((row.rate || 0) * 100)}%`;
}

export default function OwnerRoutine360Page() {
  const { routineId } = useParams();
  const navigate = useNavigate();
  const [context, setContext] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const response = await ownerApi.routineContext(routineId);
      setContext(response?.context || null);
    } catch (err) {
      setError(formatApiError(err, "No pude abrir esta rutina."));
    } finally {
      setLoading(false);
    }
  }, [routineId]);

  React.useEffect(() => { load(); }, [load]);

  const routine = context?.routine || null;
  const tracking = context?.tracking || {};

  const toggleActive = async () => {
    if (!routine || saving) return;
    setSaving(true);
    setError("");
    try {
      await ownerApi.updateRoutine(routine.id || routine._id, { active: !routine.active });
      await load();
    } catch (err) {
      setError(formatApiError(err, "No pude cambiar el estado de la rutina."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto", pb: 1 }}>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate(-1)} sx={{ ...compactActionSx, mb: 1 }}>Volver</Button>
      {error ? <Alert severity="error" sx={{ mb: 1.2 }}>{error}</Alert> : null}
      {loading ? <Box sx={{ py: 10, textAlign: "center" }}><CircularProgress size={30} /></Box> : null}
      {!loading && routine ? (
        <Stack spacing={1.2}>
          <Box sx={{ ...surfaceSx, p: { xs: 1.5, sm: 2 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.2} alignItems={{ sm: "flex-start" }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: "text.secondary", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".09em" }}>Rutina 360</Typography>
                <Typography component="h1" sx={{ mt: .25, fontSize: { xs: "1.6rem", sm: "2rem" }, fontWeight: 1000, letterSpacing: "-.04em" }}>{routine.name}</Typography>
                <Stack direction="row" spacing={.6} useFlexGap flexWrap="wrap" sx={{ mt: .8 }}>
                  <Chip size="small" label={routine.active ? "Activa" : "Pausada"} color={routine.active ? "success" : "default"} sx={{ fontWeight: 900 }} />
                  <Chip size="small" variant="outlined" label={DOMAIN_LABELS[routine.domain] || "Otro"} />
                  <Chip size="small" variant="outlined" label={`${routine.startTime}–${routine.endTime}`} />
                </Stack>
              </Box>
              <Stack direction={{ xs: "row", sm: "column" }} spacing={.6} sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}>
                <Button fullWidth size="small" variant="outlined" startIcon={<CalendarMonthRoundedIcon />} onClick={() => navigate("/dashboard/owner/calendario")} sx={compactActionSx}>Agenda</Button>
                <Button fullWidth size="small" variant={routine.active ? "text" : "contained"} startIcon={routine.active ? <PauseCircleOutlineRoundedIcon /> : <PlayCircleOutlineRoundedIcon />} onClick={toggleActive} disabled={saving} sx={compactActionSx}>{routine.active ? "Pausar" : "Activar"}</Button>
              </Stack>
            </Stack>
            <Divider sx={{ my: 1.4 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 900, color: "text.secondary" }}>DÍAS</Typography>
            <Stack direction="row" spacing={.5} useFlexGap flexWrap="wrap" sx={{ mt: .65 }}>
              {(routine.weekdays || []).map((day) => <Chip key={day} size="small" label={DAY_LABELS[day] || day} sx={{ fontWeight: 900 }} />)}
            </Stack>
            {routine.notes ? <Typography sx={{ mt: 1.35, whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5 }}>{routine.notes}</Typography> : null}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 1.1 }}>
            <Box sx={{ ...surfaceSx, p: 1.5 }}>
              <Typography sx={{ color: "text.secondary", fontSize: 11, fontWeight: 950 }}>ÚLTIMOS 30 DÍAS</Typography>
              <Typography sx={{ mt: .45, fontSize: 25, fontWeight: 1000, letterSpacing: "-.03em" }}>{trackingLabel(tracking.last30)}</Typography>
            </Box>
            <Box sx={{ ...surfaceSx, p: 1.5 }}>
              <Typography sx={{ color: "text.secondary", fontSize: 11, fontWeight: 950 }}>ÚLTIMOS 90 DÍAS</Typography>
              <Typography sx={{ mt: .45, fontSize: 25, fontWeight: 1000, letterSpacing: "-.03em" }}>{trackingLabel(tracking.last90)}</Typography>
            </Box>
          </Box>

          <Box sx={{ ...surfaceSx, p: { xs: 1.35, sm: 1.6 } }}>
            <Typography sx={{ fontWeight: 1000, fontSize: 14 }}>Últimas veces que la hiciste</Typography>
            <Stack divider={<Divider flexItem />} sx={{ mt: .6 }}>
              {(tracking.recentCheckins || []).length ? tracking.recentCheckins.slice(0, 14).map((row) => (
                <Stack key={row.id || row._id || row.dateKey} direction="row" justifyContent="space-between" spacing={1} sx={{ py: .85 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 850 }}>{dateLabel(row.dateKey)}</Typography>
                  <Chip size="small" color="success" label="Hecha" sx={{ fontWeight: 900 }} />
                </Stack>
              )) : <Typography sx={{ py: 2.5, color: "text.secondary", fontSize: 13 }}>Todavía no hay cumplimientos registrados para esta rutina.</Typography>}
            </Stack>
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
}
